// Copyright (c) Ping. All rights reserved.
//
// Apply a `com.ping.conv_state` patch that arrived from one of the account's
// OTHER devices over the DeviceGroup (multi-device conversation-state sync).
// Writes go DIRECTLY to Dexie — never through the `conversations.ts` /
// `spaces.ts` mutators, which would re-broadcast and create a device↔device
// echo loop. Mirrors the local-write side field-for-field:
//   { isMuted }            ← mute / unmute
//   { notificationLevel }  ← notification level (carries isMuted too)
//   { favorite }           ← favorite toggle
//   { archivedAt }         ← archive (ts) / unarchive (0 → cleared)
//   { pinnedAt }           ← pin (ts) / unpin (0 → cleared)
//   { deletedAt }          ← delete chat (also wipes local history)
//   { leftAt }             ← left a group (NON-destructive: keep history)
//   { folderId }           ← added to a folder
//   { folderRemoved }      ← removed from a folder
//
// ORDERING (the reason this file is more than a set of `update`s): the
// DeviceGroup gives no transport-level ordering guarantee, and a device can
// replay a backlog of patches out of order (reconnect, catch-up). A stale
// `archivedAt` arriving AFTER an `unarchive`, or an old `deletedAt` arriving
// after new activity, used to flip the conversation's state the wrong way — a
// chat vanishing / re-appearing "on its own". We now stamp each logical field
// with the newest applied event's HLC `eventTs` (in `convStateAppliedAt`) and
// drop any patch that is not strictly newer for that field. Last-writer-wins by
// CLOCK, not by arrival order.
//
// PENDING REPLAY: a patch can also arrive for a conversation this device hasn't
// materialized yet (a sibling deleted/archived a chat before our Welcome landed).
// A plain `update` no-ops on a missing row, so the change would be lost and the
// chat would later re-appear un-deleted. We stash such patches and replay them
// via `drainPendingConvState` the instant the row is created (wired into the
// incoming-conversation upsert callback).

import { log } from '@core/log/log';
import type { Conversation } from '@core/persistence/db';
import { db } from '@core/persistence/db';

/** settings_kv key holding the queued patches for a not-yet-materialized row. */
function pendingKey(conversationId: string): string {
  return `convstate.pending.${conversationId}`;
}

interface PendingConvState {
  readonly patch: Record<string, unknown>;
  readonly eventTs: number;
}

// Bound the queue so a pathological producer can't grow settings_kv without
// limit; the newest patches per field win anyway, so old overflow is harmless.
const MAX_PENDING_PER_CONVERSATION = 32;

async function stashPendingConvState(
  conversationId: string,
  patch: Record<string, unknown>,
  eventTs: number,
): Promise<void> {
  try {
    const key = pendingKey(conversationId);
    const existing = await db.settings_kv.get(key);
    const queue: PendingConvState[] = Array.isArray(existing?.value)
      ? (existing!.value as PendingConvState[])
      : [];
    queue.push({ patch, eventTs });
    const trimmed = queue.slice(-MAX_PENDING_PER_CONVERSATION);
    await db.settings_kv.put({ key, value: trimmed, updatedAt: Date.now() });
  } catch (err) {
    log.warn({ event: 'convState.stash.failed', message: (err as Error).message });
  }
}

/**
 * Replay any queued `conv_state` patches for a conversation whose row now
 * exists. Called from the incoming-conversation upsert callback so a chat that
 * was deleted/archived on another device before we joined it lands in the right
 * state the moment it materializes — instead of showing up un-deleted. Idempotent
 * and best-effort: the per-field clock in `applyConvStatePatch` makes re-applying
 * a patch a no-op.
 */
export async function drainPendingConvState(conversationId: string): Promise<void> {
  let queue: PendingConvState[] = [];
  try {
    const row = await db.settings_kv.get(pendingKey(conversationId));
    if (row === undefined || !Array.isArray(row.value)) return;
    queue = row.value as PendingConvState[];
  } catch {
    return;
  }
  if (queue.length === 0) return;
  // Only drain once the target row actually exists — otherwise keep the queue so
  // a later upsert can still apply it.
  const target = await db.conversations.get(conversationId).catch(() => undefined);
  if (target === undefined) return;
  // Oldest-first so the per-field clock converges to the newest patch.
  const ordered = [...queue].sort((a, b) => a.eventTs - b.eventTs);
  for (const p of ordered) {
    await applyConvStatePatch(conversationId, p.patch, p.eventTs);
  }
  try {
    await db.settings_kv.delete(pendingKey(conversationId));
  } catch {
    /* best-effort */
  }
}

export async function applyConvStatePatch(
  conversationId: string,
  patch: Record<string, unknown>,
  eventTs = 0,
): Promise<void> {
  try {
    // Folder add/remove ride their own mapping table and are order-independent
    // enough for our needs (a folder membership is a set flag); apply regardless
    // of whether the conversation row exists yet.
    if (typeof patch.folderId === 'string') {
      await db.conversation_folder_mappings.put({
        conversationId,
        folderId: patch.folderId,
        addedAt: Date.now(),
      });
    }
    if (typeof patch.folderRemoved === 'string') {
      await db.conversation_folder_mappings
        .where(['conversationId', 'folderId'])
        .equals([conversationId, patch.folderRemoved])
        .delete();
    }

    // Everything below mutates the conversation ROW. If it doesn't exist yet,
    // queue the patch (personal state would otherwise be silently dropped) and
    // bail — `drainPendingConvState` re-applies it on the row's first upsert.
    const row = await db.conversations.get(conversationId);
    if (row === undefined) {
      // Only stash the row-targeting fields (folder ops already applied above).
      const hasRowField =
        patch.deletedAt !== undefined ||
        patch.leftAt !== undefined ||
        patch.archivedAt !== undefined ||
        patch.pinnedAt !== undefined ||
        patch.isMuted !== undefined ||
        patch.notificationLevel !== undefined ||
        patch.favorite !== undefined;
      if (hasRowField) await stashPendingConvState(conversationId, patch, eventTs);
      return;
    }

    // Per-field monotonic clock. `eventTs <= 0` (legacy caller with no ordering
    // key) always applies, preserving old behaviour; a real HLC `eventTs` only
    // applies when it is strictly newer than the last one recorded for that field.
    const applied: Record<string, number> = { ...(row.convStateAppliedAt ?? {}) };
    const isFresh = (fieldKey: string): boolean =>
      eventTs <= 0 || eventTs > (applied[fieldKey] ?? 0);
    const mark = (fieldKey: string): void => {
      applied[fieldKey] = Math.max(applied[fieldKey] ?? 0, eventTs);
    };
    let clockChanged = false;

    // Delete-chat: wipe local history + hide the row (mirror deleteConversation).
    if (typeof patch.deletedAt === 'number' && patch.deletedAt > 0 && isFresh('deletedAt')) {
      await db.transaction('rw', db.messages, db.conversations, async () => {
        await db.messages.where('conversationId').equals(conversationId).delete();
        await db.conversations.where('id').equals(conversationId).modify((c) => {
          c.deletedAt = patch.deletedAt as number;
          delete c.lastMessagePreview;
          delete c.lastMessageAt;
          delete c.lastMessageSenderAccountId;
          (c as { unreadCount?: number }).unreadCount = 0;
        });
      });
      mark('deletedAt');
      clockChanged = true;
    }

    // Left-a-group: NON-destructive — only mark the row read-only on siblings.
    // Deliberately does NOT wipe messages or set `deletedAt` (that's the whole
    // point of "leaving keeps the old conversation"). A positive ts sets it.
    if (typeof patch.leftAt === 'number' && patch.leftAt > 0 && isFresh('leftAt')) {
      await db.conversations.update(conversationId, {
        leftAt: patch.leftAt,
      } as Partial<Conversation>);
      mark('leftAt');
      clockChanged = true;
    }

    // Archive: a positive ts sets the marker; `0` clears it (unarchive). Dexie's
    // `update` ignores `undefined`, so the clear path uses `modify` to delete the
    // key — matching `unarchive()` which strips `archivedAt` entirely.
    if (typeof patch.archivedAt === 'number' && isFresh('archivedAt')) {
      if (patch.archivedAt === 0) {
        await db.conversations
          .where('id')
          .equals(conversationId)
          .modify((c) => {
            delete c.archivedAt;
          });
      } else {
        await db.conversations.update(conversationId, { archivedAt: patch.archivedAt });
      }
      mark('archivedAt');
      clockChanged = true;
    }

    // Pin: positive ts sets the marker; `0` clears it (unpin) — same
    // set/clear shape as archive above.
    if (typeof patch.pinnedAt === 'number' && isFresh('pinnedAt')) {
      if (patch.pinnedAt === 0) {
        await db.conversations
          .where('id')
          .equals(conversationId)
          .modify((c) => {
            delete c.pinnedAt;
          });
      } else {
        await db.conversations.update(conversationId, { pinnedAt: patch.pinnedAt });
      }
      mark('pinnedAt');
      clockChanged = true;
    }

    // Plain conversation-row fields (straight sets). Mute + notification level
    // share one clock key ('mute') since a level change carries `isMuted` too.
    const rowPatch: Record<string, unknown> = {};
    const muteFresh = isFresh('mute');
    if (muteFresh && typeof patch.isMuted === 'boolean') rowPatch.isMuted = patch.isMuted;
    if (
      muteFresh &&
      (patch.notificationLevel === 'all' ||
        patch.notificationLevel === 'mentions' ||
        patch.notificationLevel === 'off')
    ) {
      rowPatch.notificationLevel = patch.notificationLevel;
    }
    if (isFresh('favorite') && typeof patch.favorite === 'boolean') {
      rowPatch.favorite = patch.favorite;
    }
    if (Object.keys(rowPatch).length > 0) {
      await db.conversations.update(conversationId, rowPatch as Partial<Conversation>);
      if ('isMuted' in rowPatch || 'notificationLevel' in rowPatch) mark('mute');
      if ('favorite' in rowPatch) mark('favorite');
      clockChanged = true;
    }

    // Persist the advanced per-field clock so a later replay/reload can't
    // re-apply an already-superseded patch.
    if (clockChanged) {
      await db.conversations.update(conversationId, {
        convStateAppliedAt: applied,
      } as Partial<Conversation>);
    }
    log.info({ event: 'convState.applied', conversationIdPrefix: conversationId.slice(0, 8) });
  } catch (err) {
    log.warn({ event: 'convState.apply.failed', message: (err as Error).message });
  }
}
